import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { MY_SOCIAL_CONNECTIONS } from '@/queries/queries';
import { PUBLISH_CLIP_TO_FACEBOOK } from '@/queries/publishing';
import { CREATE_MY_SCHEDULED_PUBLISH } from '@/queries/scheduledPublishing';
import {
  datetimeLocalValueToIso,
  viewerTimeZone,
} from '@/lib/publishing/calendarMonth';
import { isScheduledAtTooSoon } from '@/lib/publishing/isScheduledAtTooSoon';
import { minScheduleDatetimeLocal } from '@/lib/publishing/minScheduleDatetimeLocal';
import { isFacebookPublishableConnection } from '@/lib/publishing/resolvePublishEligibility';
import { extractPublishErrorCode, mapPublishError } from '@/lib/publishing/mapPublishError';
import type { PublishConnection, PublishDialogTarget } from '@/lib/publishing/types';
import { PublishWhenControl, type PublishWhen } from '@/components/publishing/PublishWhenControl';

const CAPTION_LIMIT = 2200;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clipId: string;
  clipTitle: string;
  initialWhen?: PublishWhen;
  initialDatetimeLocal?: string;
  onPublished?: (target: PublishDialogTarget) => void;
}

export const PublishToFacebookDialog: React.FC<Props> = ({
  open, onOpenChange, clipId, clipTitle, initialWhen, initialDatetimeLocal, onPublished,
}) => {
  const { t } = useTranslation();
  const [connectionId, setConnectionId] = useState('');
  const [caption, setCaption] = useState(clipTitle || '');
  const [when, setWhen] = useState<PublishWhen>('now');
  const [datetimeLocal, setDatetimeLocal] = useState('');
  const [whenError, setWhenError] = useState<string | null>(null);
  const targetVersionRef = useRef(0);
  const mountedRef = useRef(false);
  const currentTargetRef = useRef({ open, clipId });
  currentTargetRef.current = { open, clipId };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    targetVersionRef.current += 1;
    setConnectionId('');
    setCaption((clipTitle || '').slice(0, CAPTION_LIMIT));
    setWhen(initialWhen ?? 'now');
    setDatetimeLocal(initialDatetimeLocal ?? '');
    setWhenError(null);
  }, [clipId, clipTitle, open, initialWhen, initialDatetimeLocal]);

  const { data: connData } = useQuery(MY_SOCIAL_CONNECTIONS, { skip: !open });
  const [publish, { loading }] = useMutation(PUBLISH_CLIP_TO_FACEBOOK);
  const [createScheduled, { loading: scheduling }] = useMutation(CREATE_MY_SCHEDULED_PUBLISH);
  const timeZone = viewerTimeZone();
  const minDatetimeLocal = minScheduleDatetimeLocal(new Date(), timeZone);
  const submitting = loading || scheduling;

  const connections: PublishConnection[] = useMemo(
    () => (connData?.mySocialConnections || [])
      .filter((c: PublishConnection) => c.platform === 'facebook'),
    [connData],
  );
  const publishable = useMemo(
    () => connections.filter(isFacebookPublishableConnection),
    [connections],
  );
  const selected = connectionId || publishable[0]?.id || '';

  const close = () => {
    targetVersionRef.current += 1;
    onOpenChange(false);
  };

  const goToSettings = () => {
    close();
    window.dispatchEvent(new CustomEvent('autoclipper:open-account-settings', {
      detail: { section: 'social', platform: 'facebook' },
    }));
  };

  const handlePublish = async () => {
    const submittedClipId = clipId;
    const submittedVersion = targetVersionRef.current;
    const submittedTarget: PublishDialogTarget = {
      clipId: submittedClipId,
      platform: 'facebook',
    };
    const isCurrentSubmission = () => (
      mountedRef.current
      && submittedVersion === targetVersionRef.current
      && currentTargetRef.current.open
      && currentTargetRef.current.clipId === submittedClipId
    );

    try {
      if (when === 'later') {
        const scheduledAt = datetimeLocalValueToIso(datetimeLocal, timeZone);
        if (!scheduledAt || isScheduledAtTooSoon(scheduledAt, new Date())) {
          setWhenError(t('publishing.schedule.tooSoon'));
          return;
        }
        await createScheduled({
          variables: {
            input: {
              clipId: submittedClipId,
              socialConnectionId: selected,
              platform: 'facebook',
              scheduledAt,
              caption: caption.trim() || null,
              title: null,
              description: null,
              privacyStatus: null,
              madeForKids: null,
            },
          },
        });
        if (!isCurrentSubmission()) return;
        toast.success(t('publishing.schedule.saved'));
        close();
        return;
      }

      const result = await publish({
        variables: {
          clipId: submittedClipId,
          socialConnectionId: selected,
          caption: caption.trim() || null,
        },
      });
      const record = result.data?.publishClipToFacebook;
      const accepted = !!record && record.status !== 'failed';
      if (accepted) onPublished?.(submittedTarget);
      if (!isCurrentSubmission()) return;
      if (record?.status === 'failed') {
        toast.error(t(mapPublishError(record.fail_reason)));
        return;
      }
      toast.success(t('publishing.facebook.success'));
      close();
    } catch (err) {
      if (!isCurrentSubmission()) return;
      toast.error(t(mapPublishError(extractPublishErrorCode(err))));
    }
  };

  const canSubmit = publishable.length > 0
    && !!selected
    && caption.length <= CAPTION_LIMIT
    && (when === 'now' || !!datetimeLocal);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) close(); }}>
      <DialogContent data-testid="publish-facebook-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('publishing.facebook.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('publishing.facebook.dialogDescription')}</DialogDescription>
        </DialogHeader>
        {connections.length === 0 ? (
          <div className="space-y-3" data-testid="facebook-no-connection">
            <p className="text-sm text-muted-foreground">{t('publishing.facebook.noConnectionBody')}</p>
            <Button onClick={goToSettings}>{t('publishing.facebook.accountSettingsAction')}</Button>
          </div>
        ) : publishable.length === 0 ? (
          <div className="space-y-3" data-testid="facebook-reconnect">
            <p className="text-sm text-muted-foreground">{t('publishing.facebook.reconnectBody')}</p>
            <Button onClick={goToSettings}>{t('publishing.facebook.accountSettingsAction')}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium">{t('publishing.facebook.accountLabel')}</label>
              <Select value={selected} onValueChange={setConnectionId}>
                <SelectTrigger data-testid="facebook-account-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {publishable.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.display_name || c.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">{t('publishing.facebook.captionLabel')}</label>
              <Textarea
                data-testid="facebook-caption"
                value={caption}
                maxLength={CAPTION_LIMIT}
                onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_LIMIT))}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {t('publishing.youtube.characterCount', {
                  current: caption.length,
                  max: CAPTION_LIMIT,
                })}
              </p>
            </div>
            <PublishWhenControl
              idPrefix="facebook"
              when={when}
              onWhenChange={(next) => {
                setWhen(next);
                setWhenError(null);
                if (next === 'later' && !datetimeLocal) setDatetimeLocal(minDatetimeLocal);
              }}
              datetimeLocal={datetimeLocal}
              onDatetimeLocalChange={(value) => {
                setDatetimeLocal(value);
                setWhenError(null);
              }}
              minDatetimeLocal={minDatetimeLocal}
              error={whenError}
            />
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close}>
            {t('publishing.facebook.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="facebook-publish-submit"
            disabled={!canSubmit || submitting}
            onClick={handlePublish}
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {submitting ? t('publishing.facebook.publishing') : t('publishing.facebook.publish')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublishToFacebookDialog;
